// IntakeForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../Stylesheets/IntakeForm.css";
import TermsModal from "./TermsModal";
import TypewriterCycle from "./Utils/Typewriter";
import { useGym } from "./GymContext";
import WaiverButton from "./WaiverButton";

const API_URL = "https://worker-consolidated.maxli5004.workers.dev/intake-form";
const FALLBACK_TERMS_URL = "/terms.html";
const TRIAL_DAYS = 7;
const TIME_ZONE = "America/Toronto";

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatLongDate(date) {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: TIME_ZONE,
  }).format(date);
}

function formatPhone(value) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function onlyDigitsPhone(value) {
  return (value || "").replace(/\D/g, "").slice(0, 10);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function toTitleCaseName(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/(^|\s|-|')([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

function SuccessMark() {
  return (
    <svg className="if-check" viewBox="0 0 52 52" aria-hidden="true">
      <circle className="if-check-circle" cx="26" cy="26" r="25" fill="none" />
      <path className="if-check-path" fill="none" d="M14 27 L22 35 L38 19" />
    </svg>
  );
}

function PerkIcon() {
  return (
    <span className="if-perk-icon" aria-hidden="true">
      <svg viewBox="0 0 10 10">
        <polyline points="1.5,5 4,7.5 8.5,2.5" />
      </svg>
    </span>
  );
}

export default function IntakeForm() {
  const { tenantKey, brand } = useGym();
  const xMaple = brand?.xMapleHeaderValue ?? (tenantKey === "maple" ? "true" : "false");
  const TERMS_URL = brand?.termsUrl || FALLBACK_TERMS_URL;
  const logoUrl = brand?.logoUrl;

  const firstInputRef = useRef(null);

  // Kiosk: auto-focus first field so the form is immediately ready for input
  useEffect(() => {
    const t = setTimeout(() => firstInputRef.current?.focus(), 400);
    return () => clearTimeout(t);
  }, []);

  const [trialEndsLabel, setTrialEndsLabel] = useState("");
  const [transparentCheckMark, setTransparentCheckMark] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [showCheckMark, setShowCheckMark] = useState(false);
  const [successFirstName, setSuccessFirstName] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    agree: false,
  });

  const phoneDigits = useMemo(() => onlyDigitsPhone(form.phone), [form.phone]);

  const isValid = useMemo(() => {
    return (
      form.firstName.trim().length > 0 &&
      form.lastName.trim().length > 0 &&
      isValidEmail(form.email) &&
      phoneDigits.length === 10 &&
      form.agree === true
    );
  }, [form.firstName, form.lastName, form.email, phoneDigits, form.agree]);

  useEffect(() => {
    if (status !== "success") return;

    setTransparentCheckMark(false);

    const dimCheckMark = setTimeout(() => setTransparentCheckMark(true), 1500);
    const hideCheck = setTimeout(() => setShowCheckMark(false), 2000);

    return () => {
      clearTimeout(dimCheckMark);
      clearTimeout(hideCheck);
    };
  }, [status]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "phone") {
      setForm((p) => ({ ...p, phone: formatPhone(value) }));
      return;
    }
    if (type === "checkbox") {
      setForm((p) => ({ ...p, [name]: checked }));
      return;
    }
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || status === "submitting") return;

    setStatus("submitting");
    setErrorMsg("");

    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: phoneDigits,
        agreedToTerms: true,
        kiosk: true,
      };

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-maple": xMaple },
        mode: "cors",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        let msg = "Could not submit. Please try again.";
        try { const j = JSON.parse(txt); msg = j?.message || msg; } catch (_) {}
        setErrorMsg(msg);
        setStatus("error");
        return;
      }

      await res.json().catch(() => ({}));

      const first = toTitleCaseName(form.firstName);
      setSuccessFirstName(first);
      setTransparentCheckMark(false);
      setShowCheckMark(true);
      setStatus("success");

      const endsAt = addDays(new Date(), TRIAL_DAYS);
      setTrialEndsLabel(formatLongDate(endsAt));

      setForm({ firstName: "", lastName: "", email: "", phone: "", agree: false });
    } catch (err) {
      setErrorMsg("Network error — please check connection and try again.");
      setStatus("error");
    }
  };

  const greetName = successFirstName ? ` ${successFirstName}` : "";

  return (
    <div className="if-page" data-tenant={tenantKey}>
      {/* Animated background orbs */}
      <div className="if-orb if-orb--1" aria-hidden="true" />
      <div className="if-orb if-orb--2" aria-hidden="true" />
      <div className="if-orb if-orb--3" aria-hidden="true" />

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} termsUrl={TERMS_URL} />

      {status === "success" ? (
        <div
          className="if-success-screen"
          style={{ backgroundColor: brand?.successColor || brand?.themeColor || "#000" }}
        >
          {/* Orbs on success screen too */}
          <div className="if-orb if-orb--1" aria-hidden="true" />
          <div className="if-orb if-orb--2" aria-hidden="true" />

          {showCheckMark ? (
            <div className={`if-check-wrap${transparentCheckMark ? " if-transparent" : ""}`}>
              <SuccessMark />
            </div>
          ) : (
            <div className="if-success">
              <img
                className="if-secondary-logo"
                src={logoUrl}
                alt={brand?.siteName || "Gym"}
              />
              <div className="if-success-line-1">
                <TypewriterCycle text={`Thank you${greetName}!`} />
              </div>
              <div className="if-success-line-2">
                <TypewriterCycle
                  startDelay={900}
                  text={`Your free trial is active until ${trialEndsLabel}.`}
                />
              </div>
              <div className="if-success-divider" />
              <div className="if-success-line-3">
                <TypewriterCycle
                  startDelay={2800}
                  text="Last step — sign the waiver below ↓"
                />
              </div>
              <WaiverButton href={brand?.waiver} />
            </div>
          )}
        </div>
      ) : (
        <div className="if-card">
          {/* ── Left brand panel ── */}
          <div
            className={`if-left${brand?.leftTheme === "light" ? " if-left--light" : ""}`}
            style={{ backgroundColor: brand?.themeColor || "#000" }}
          >
            <div className="if-left-accent" aria-hidden="true" />

            <div className="if-logo-section">
              <img className="if-logo" src={logoUrl} alt={brand?.siteName || "Gym"} />
            </div>

            <div className="if-left-body">
              <div className="if-trial-display">
                <span className="if-trial-number">7</span>
                <span className="if-trial-unit">Day Free Trial</span>
              </div>

              <div className="if-left-divider" />

              <div className="if-perks">
                <div className="if-perk">
                  <PerkIcon />
                  No commitment required
                </div>
                <div className="if-perk">
                  <PerkIcon />
                  Access all classes for 7 days
                </div>
                <div className="if-perk">
                  <PerkIcon />
                  $50 OFF if you decide to join
                </div>
                <div className="if-perk">
                  <PerkIcon />
                  Beginner-friendly coaches
                </div>
              </div>
            </div>

            <div className="if-trial-badge">
              <div className="if-badge-row">
                <span className="if-badge-value">$50</span>
                <span className="if-badge-label">OFF when<br />you join</span>
              </div>
            </div>

            {/* Mobile-only label (shown when left panel collapses to header) */}
            <span className="if-trial-mobile-label">7 Day Free Trial</span>
          </div>

          {/* ── Right form panel ── */}
          <div className="if-right">
            <div className="if-top">
              <span className="if-eyebrow">Welcome</span>
              <h2 className="if-title">
                Activate Your{" "}
                <span className="if-title-gradient">Free Trial</span>
              </h2>
              <p className="if-subtitle">Fill in your details below to get started.</p>
            </div>

            {status === "error" && (
              <div className="if-error" role="alert">{errorMsg}</div>
            )}

            <form className="if-form" onSubmit={handleSubmit} autoComplete="off">
              <div className="if-grid">
                {/* First name */}
                <div className="if-field">
                  <input
                    ref={firstInputRef}
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    inputMode="text"
                  />
                  <label htmlFor="firstName">First Name</label>
                </div>

                {/* Last name */}
                <div className="if-field">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    inputMode="text"
                  />
                  <label htmlFor="lastName">Last Name</label>
                </div>

                {/* Email */}
                <div className="if-field if-span-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                  />
                  <label htmlFor="email">Email Address</label>
                </div>

                {/* Phone */}
                <div className="if-field if-span-2">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder=" "
                    required
                    inputMode="tel"
                  />
                  <label htmlFor="phone">Phone Number</label>
                </div>
              </div>

              {/* Custom checkbox */}
              <label className="if-check-row">
                <input
                  type="checkbox"
                  name="agree"
                  className="if-check-hidden"
                  checked={form.agree}
                  onChange={handleChange}
                  required
                />
                <span className={`if-check-box${form.agree ? " checked" : ""}`} aria-hidden="true">
                  <svg viewBox="0 0 13 13">
                    <polyline points="2,6.5 5.5,10 11,3" />
                  </svg>
                </span>
                <span className="if-check-text">
                  I agree to the{" "}
                  <a
                    href={TERMS_URL || "#"}
                    onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
                  >
                    Terms &amp; Conditions
                  </a>
                  .
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                className="if-primary-btn"
                disabled={!isValid || status === "submitting"}
              >
                {status === "submitting" ? (
                  <span className="if-btn-inline">
                    <span className="if-spinner" aria-hidden="true" />
                    Submitting…
                  </span>
                ) : (
                  <span className="if-btn-inline">Start Free Trial</span>
                )}
              </button>

              <p className="if-legal">
                By starting your free trial, you agree to receive automated
                email/text reminders about your trial start and end dates.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
